import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { useTask } from "../../../../../hooks/query";
import { useAuthorization } from "../../../../../hooks";
import { PERMISSIONS } from "../../../../../constants/permissions";

export default function useTaskHandler() {
    const [activeTask, setActiveTask] = useState<null | any>(null);
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [taskList, setTaskList] = useState<any[]>([]);

    const { can } = useAuthorization();
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const id = params.id as string;

    const sanitizeUrlParam = (param: string) => {
        return param.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
    };

    const { useListByProjectId, usePatch } = useTask();
    const listTasksByProjectSvc = useListByProjectId();
    const patchTaskSvc = usePatch();

    const handleClickOnProgressBar = (activeStep: any) => {
        if (taskList.length === 0) {
            return;
        }
        const currentIndex = taskList.findIndex((step: any) => step.Name === activeStep.Name);
        setCurrentStep(currentIndex);
        setActiveTask(activeStep);

        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            params.set("taskName", sanitizeUrlParam(activeStep.Name));
            return params;
        });
    };

    const markTaskCompleteAndProceed = async (taskId: string) => {
        try {
            await patchTaskSvc.mutateAsync({ Id: taskId, Status: "COMPLETED" });

            const currentIndex = taskList.findIndex((step: any) => step.Id === taskId);
            if (currentIndex !== -1 && currentIndex + 1 < taskList.length) {
                const nextTask = taskList[currentIndex + 1];
                handleClickOnProgressBar(nextTask);
            }
            // Refresh task list
            if (id) {
                await firstDataLoad();
            }
        } catch (error) {
            console.error("Failed to mark task as completed", error);
        }
    };

    const firstDataLoad = async () => {
        try {
            if (!id) {
                return;
            }
            const queryParams = { ProjectId: id };
            const response = await listTasksByProjectSvc.mutateAsync(queryParams);
            const sortedTasks = response.sort((a: any, b: any) => a.SortOrder - b.SortOrder);

            const getRequiredPermissions = (taskName: string): string[] => {
                const name = (taskName || "").toLowerCase();
                if (name.includes("initial inspection")) return [PERMISSIONS.TASK_INITIAL_INSPECTION];
                if (name.includes("estimate/contract"))
                    return [PERMISSIONS.TASK_INITIAL_ESTIMATE, PERMISSIONS.TASK_INITIAL_CONTRACT_SIGNING];
                if (name.includes("contract signing")) return [PERMISSIONS.TASK_INITIAL_CONTRACT_SIGNING];
                if (name.includes("material ordering")) return [PERMISSIONS.TASK_INITIAL_MATERIAL_ORDERING];
                if (name.includes("job execution")) return [PERMISSIONS.TASK_INITIAL_JOB_EXECUTION];
                if (name.includes("final settlement")) return [PERMISSIONS.TASK_INITIAL_FINAL_SETTLEMENT];
                return [];
            };

            const permittedTasks = sortedTasks.filter((task: any) => {
                const required = getRequiredPermissions(task?.Name);
                if (required.length === 0) return true;
                return required.some((perm) => can(perm as any));
            });

            setTaskList(permittedTasks);

            const initialTaskName = searchParams.get("taskName");
            if (initialTaskName) {
                const task = permittedTasks.find((t: any) => sanitizeUrlParam(t.Name) === initialTaskName);
                if (task) {
                    const taskIndex = permittedTasks.findIndex((t: any) => sanitizeUrlParam(t.Name) === initialTaskName);
                    setCurrentStep(taskIndex);
                    setActiveTask(task);
                } else {
                    setActiveTask(permittedTasks[0]);
                }
            } else {
                setActiveTask(permittedTasks[0]);
            }
        } catch (error) {
            console.error("Error fetching tasks:", error);
        }
    };

    useEffect(() => {
        firstDataLoad().then();
    }, [id]);

    return {
        handleClickOnProgressBar,
        setActiveTask,
        activeTask,
        currentStep,
        setCurrentStep,
        taskList,
        listTasksByProjectSvc,
        markTaskCompleteAndProceed,
    };
}
